"use client";

import { useMemo, useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import {
  TURKISH_CASE_STRINGS,
  TurkishCaseToggle,
  useTurkishCase,
} from "@/components/tools/turkish-case-toggle";
import { writeToClipboard } from "@/lib/clipboard";
import { capitalizeFirst, foldLower } from "@/lib/turkish-case";

/* ------------------------------------------------------------------ */
/*  Regex constants (module level per Biome rule)                     */
/* ------------------------------------------------------------------ */

const RE_NON_ALPHANUMERIC_HYPHEN = /[^a-z0-9-]/g;
const RE_NON_ALPHANUMERIC_UNDERSCORE = /[^a-z0-9_]/g;
const RE_MULTIPLE_HYPHENS = /-{2,}/g;
const RE_MULTIPLE_UNDERSCORES = /_{2,}/g;
const RE_LEADING_TRAILING_HYPHENS = /^-+|-+$/g;
const RE_LEADING_TRAILING_UNDERSCORES = /^_+|_+$/g;
const RE_WHITESPACE = /\s+/g;
const RE_COMBINING_MARKS = /\p{M}/gu;

/* ------------------------------------------------------------------ */
/*  Diacritics map for transliteration                                */
/* ------------------------------------------------------------------ */

const DIACRITICS_MAP: Record<string, string> = {
  à: "a",
  á: "a",
  â: "a",
  ã: "a",
  ä: "a",
  å: "a",
  æ: "ae",
  ç: "c",
  è: "e",
  é: "e",
  ê: "e",
  ë: "e",
  ì: "i",
  í: "i",
  î: "i",
  ï: "i",
  ð: "d",
  ñ: "n",
  ò: "o",
  ó: "o",
  ô: "o",
  õ: "o",
  ö: "o",
  ø: "o",
  ù: "u",
  ú: "u",
  û: "u",
  ü: "u",
  ý: "y",
  ÿ: "y",
  ß: "ss",
  ł: "l",
  ž: "z",
  š: "s",
  č: "c",
  ř: "r",
  ż: "z",
  ą: "a",
  ę: "e",
  ń: "n",
  ś: "s",
  ć: "c",
  // Turkish. `ç`, `ö` and `ü` are already above, and `İ` folds to `i` before it gets
  // here, which leaves exactly three letters the map was missing. Their absence was a
  // real defect with no locale involved: the ASCII filter below strips whatever is
  // unmapped, so "İstanbul Işık" slugged to "istanbul-ik" — two letters silently
  // deleted. Lower-case keys only, matching the rest of the map: `transliterate` runs
  // after the fold, never before it.
  ı: "i",
  ş: "s",
  ğ: "g",
  // Letters that are not a base letter plus a combining mark, so Unicode
  // decomposition cannot reach them. Anything that is (ť ů ň ễ …) is decomposed below.
  đ: "d",
  ħ: "h",
  œ: "oe",
  þ: "th",
};

/* ------------------------------------------------------------------ */
/*  Slug generation logic                                             */
/* ------------------------------------------------------------------ */

/** Exported for , which pins the Turkish letters. */
export function transliterate(str: string): string {
  let result = "";
  for (const char of str) {
    result +=
      DIACRITICS_MAP[char] ??
      char.normalize("NFD").replace(RE_COMBINING_MARKS, "");
  }
  return result;
}

interface SlugResult {
  kebab: string;
  snake: string;
  camel: string;
  inputLength: number;
  slugLength: number;
}

function generateSlugs(text: string, turkish: boolean): SlugResult | null {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return null;
  }

  const lower = transliterate(foldLower(trimmed, turkish));

  // Kebab-case
  const kebab = lower
    .replace(RE_WHITESPACE, "-")
    .replace(RE_NON_ALPHANUMERIC_HYPHEN, "")
    .replace(RE_MULTIPLE_HYPHENS, "-")
    .replace(RE_LEADING_TRAILING_HYPHENS, "");

  // Snake_case
  const snake = lower
    .replace(RE_WHITESPACE, "_")
    .replace(RE_NON_ALPHANUMERIC_UNDERSCORE, "")
    .replace(RE_MULTIPLE_UNDERSCORES, "_")
    .replace(RE_LEADING_TRAILING_UNDERSCORES, "");

  // camelCase
  const words = kebab.split("-").filter(Boolean);
  const camel =
    words.length > 0
      ? words[0] +
        words
          .slice(1)
          .map((w) => capitalizeFirst(w, turkish))
          .join("")
      : "";

  return {
    kebab,
    snake,
    camel,
    inputLength: trimmed.length,
    slugLength: kebab.length,
  };
}

/* ------------------------------------------------------------------ */
/*  English strings. A translated content file overrides these        */
/*  through `ui`.                                                     */
/* ------------------------------------------------------------------ */

export const TEXT_TO_SLUG_STRINGS = {
  ...TURKISH_CASE_STRINGS,
  enterYourText: "Enter your text",
  placeholder: "Type or paste text to convert to a slug...",
  inputCharacters: "Input Characters",
  slugCharacters: "Slug Characters",
  generatedSlugs: "Generated Slugs",
  kebabLabel: "kebab-case (recommended)",
  kebabDescription:
    "Recommended for URLs. Words separated by hyphens. Best for SEO.",
  snakeLabel: "snake_case",
  snakeDescription:
    "Words separated by underscores. Common in file names and databases.",
  camelLabel: "camelCase",
  camelDescription:
    "No separator, each word capitalized except the first. Common in programming.",
  urlPreview: "URL Preview",
  urlPrefix: "https://example.com/",
  copied: "Copied!",
  copy: "Copy",
} as const;

/* ------------------------------------------------------------------ */
/*  Copy button component                                             */
/* ------------------------------------------------------------------ */

function CopyButton({
  text,
  copyLabel,
  copiedLabel,
}: {
  text: string;
  copyLabel: string;
  copiedLabel: string;
}) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    writeToClipboard(text).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <button
      className="rounded-md border border-border bg-background px-3 py-1 text-muted-foreground text-xs hover:bg-muted hover:text-foreground"
      onClick={handleCopy}
      type="button"
    >
      {copied ? copiedLabel : copyLabel}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  Slug output card                                                  */
/* ------------------------------------------------------------------ */

function SlugCard({
  label,
  value,
  description,
  copyLabel,
  copiedLabel,
}: {
  label: string;
  value: string;
  description: string;
  copyLabel: string;
  copiedLabel: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="font-medium text-foreground text-sm">{label}</h3>
        <CopyButton
          copiedLabel={copiedLabel}
          copyLabel={copyLabel}
          text={value}
        />
      </div>
      <p className="break-all rounded bg-muted/50 p-3 font-mono text-foreground text-sm">
        {value}
      </p>
      <p className="mt-2 text-muted-foreground text-xs">{description}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Widget Component                                                  */
/* ------------------------------------------------------------------ */

export default function TextToSlugWidget() {
  const strings = useToolUi(TEXT_TO_SLUG_STRINGS);
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [turkish, setTurkish] = useTurkishCase();

  const result = useMemo(() => generateSlugs(text, turkish), [text, turkish]);

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="slug-input"
        >
          {strings.enterYourText}
        </label>
        <input
          className="w-full rounded-lg border border-border bg-card p-4 text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="slug-input"
          onChange={(e) => setText(e.target.value)}
          placeholder={strings.placeholder}
          type="text"
          value={text}
        />
        <TurkishCaseToggle
          checked={turkish}
          onChange={setTurkish}
          strings={strings}
        />
      </div>

      {/* Stats */}
      {result && (
        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-lg border border-border bg-card p-4 text-center">
            <p className="font-semibold text-2xl text-foreground">
              {fmt(result.inputLength)}
            </p>
            <p className="mt-1 text-muted-foreground text-sm">
              {strings.inputCharacters}
            </p>
          </div>
          <div className="rounded-lg border border-border bg-card p-4 text-center">
            <p className="font-semibold text-2xl text-foreground">
              {fmt(result.slugLength)}
            </p>
            <p className="mt-1 text-muted-foreground text-sm">
              {strings.slugCharacters}
            </p>
          </div>
        </div>
      )}

      {/* Slug outputs */}
      {result && (
        <section className="space-y-4">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.generatedSlugs}
          </h2>
          <div className="space-y-4">
            <SlugCard
              copiedLabel={strings.copied}
              copyLabel={strings.copy}
              description={strings.kebabDescription}
              label={strings.kebabLabel}
              value={result.kebab}
            />
            <SlugCard
              copiedLabel={strings.copied}
              copyLabel={strings.copy}
              description={strings.snakeDescription}
              label={strings.snakeLabel}
              value={result.snake}
            />
            <SlugCard
              copiedLabel={strings.copied}
              copyLabel={strings.copy}
              description={strings.camelDescription}
              label={strings.camelLabel}
              value={result.camel}
            />
          </div>
        </section>
      )}

      {/* URL preview */}
      {result && result.kebab.length > 0 && (
        <div className="rounded-lg border border-border bg-card p-4">
          <h3 className="mb-2 font-medium text-foreground text-sm">
            {strings.urlPreview}
          </h3>
          <p className="break-all rounded bg-muted/50 p-3 font-mono text-muted-foreground text-sm">
            {strings.urlPrefix}
            <span className="text-foreground">{result.kebab}</span>
          </p>
        </div>
      )}
    </>
  );
}
